import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GlobalStyle from "./GlobalStyle";
import { FaEdit, FaTrash, FaPlus, FaUserShield, FaUser, FaKey, FaLock } from "react-icons/fa";

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
  bg: "#f8fafc",
  card: "#ffffff",
  primary: "#6366f1",
  primaryDark: "#4f46e5",
  primaryLight: "#eef2ff",
  danger: "#ef4444",
  dangerLight: "#fef2f2",
  success: "#10b981",
  successLight: "#ecfdf5",
  text: "#0f172a",
  textSec: "#475569",
  muted: "#94a3b8",
  border: "#e2e8f0",
  borderHover: "#cbd5e1",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -1px rgba(0,0,0,0.04)",
};

const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
`;

const fadeIn = keyframes`from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); }`;

// ─── Layout ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  font-family: 'Inter', 'Segoe UI', sans-serif;
  background: ${C.bg};
  min-height: 100vh;
  padding: 32px 28px;
  color: ${C.text};
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 28px;
  gap: 12px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: ${C.text};
  letter-spacing: -0.3px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 24px;
  align-items: start;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// ─── Card ─────────────────────────────────────────────────────────────────────
const Card = styled.div`
  background: ${C.card};
  border: 1px solid ${C.border};
  border-radius: 14px;
  padding: 24px;
  box-shadow: ${C.shadow};
  animation: ${fadeIn} 0.2s ease;
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${C.text};
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px solid ${C.border};
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ─── Form ─────────────────────────────────────────────────────────────────────
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 18px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${C.textSec};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 5px;
  display: block;
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 12px;
  color: ${C.muted};
  font-size: 13px;
  pointer-events: none;
  display: flex;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px 9px ${({ hasIcon }) => hasIcon ? "34px" : "12px"};
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  font-size: 14px;
  color: ${C.text};
  background: ${C.bg};
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
  &:focus {
    border-color: ${C.primary};
    box-shadow: 0 0 0 3px ${C.primaryLight};
    background: #fff;
  }
  &::placeholder { color: ${C.muted}; }
`;

const RoleToggle = styled.div`
  display: flex;
  gap: 0;
  border: 1.5px solid ${C.border};
  border-radius: 8px;
  overflow: hidden;
`;

const RoleOption = styled.button`
  flex: 1;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s;
  background: ${({ active }) => active ? C.primary : "transparent"};
  color: ${({ active }) => active ? "#fff" : C.textSec};
  &:hover {
    background: ${({ active }) => active ? C.primaryDark : C.bg};
  }
`;

const PasswordHint = styled.div`
  font-size: 11px;
  color: ${C.muted};
  margin-top: 4px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────
const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
  font-family: inherit;

  ${({ variant }) => variant === "primary" && `
    background: #6366f1;
    color: #fff;
    &:hover { background: #4f46e5; }
  `}
  ${({ variant }) => variant === "ghost" && `
    background: transparent;
    color: #475569;
    border: 1.5px solid #e2e8f0;
    &:hover { background: #f8fafc; border-color: #cbd5e1; color: #0f172a; }
  `}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const IconBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: all 0.15s;
  ${({ variant }) => variant === "edit" && `
    background: #eef2ff;
    color: #6366f1;
    &:hover { background: #e0e7ff; }
  `}
  ${({ variant }) => variant === "delete" && `
    background: #fef2f2;
    color: #ef4444;
    &:hover { background: #fde8e8; }
  `}
`;

// ─── User list ────────────────────────────────────────────────────────────────
const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const UserCard = styled.div`
  background: ${C.card};
  border: 1.5px solid ${({ editing }) => editing ? C.primary : C.border};
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: border-color 0.15s, box-shadow 0.15s;
  animation: ${fadeIn} 0.18s ease;
  &:hover {
    border-color: ${C.borderHover};
    box-shadow: ${C.shadowMd};
  }
`;

const UserAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ isAdmin }) => isAdmin ? C.primary : "#64748b"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${C.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: ${({ isAdmin }) => isAdmin ? C.primaryLight : "#f1f5f9"};
  color: ${({ isAdmin }) => isAdmin ? C.primary : C.textSec};
`;

const UserActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${C.muted};
  font-size: 14px;
  line-height: 1.6;
`;

// ─── Access denied ────────────────────────────────────────────────────────────
const DeniedWrap = styled.div`
  font-family: 'Inter', 'Segoe UI', sans-serif;
  background: ${C.bg};
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeniedCard = styled.div`
  background: ${C.card};
  border: 1px solid ${C.border};
  border-radius: 16px;
  padding: 40px 48px;
  text-align: center;
  box-shadow: ${C.shadowMd};
  max-width: 380px;
`;

// ─── Component ────────────────────────────────────────────────────────────────
function Utenti({ userRole }) {
  const [users, setUsers]         = useState([]);
  const [username, setUsername]   = useState("");
  const [password, setPassword]   = useState("");
  const [role, setRole]           = useState("user");
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { fetchUsers(); }, []);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const fetchUsers = async () => {
    try {
      const res = await axios.get("/api/users", { headers: authHeader() });
      setUsers(res.data);
    } catch {
      toast.error("Errore nel caricamento utenti");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { username, password, role };
    try {
      if (editingId) {
        await axios.put(`/api/users/${editingId}`, data, { headers: authHeader() });
        toast.success("Utente aggiornato");
      } else {
        await axios.post("/api/users", data, { headers: authHeader() });
        toast.success("Utente creato");
      }
      resetForm();
      fetchUsers();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const resetForm = () => {
    setUsername(""); setPassword(""); setRole("user"); setEditingId(null);
  };

  const handleEdit = (u) => {
    setUsername(u.username); setRole(u.role); setPassword(""); setEditingId(u.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo utente?")) return;
    try {
      await axios.delete(`/api/users/${id}`, { headers: authHeader() });
      toast.success("Utente eliminato");
      fetchUsers();
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  if (userRole !== "admin") {
    return (
      <>
        <GlobalStyle />
        <Fonts />
        <DeniedWrap>
          <DeniedCard>
            <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>
              Accesso negato
            </div>
            <div style={{ fontSize: 14, color: C.textSec, lineHeight: 1.6 }}>
              Questa pagina è riservata agli amministratori.
            </div>
          </DeniedCard>
        </DeniedWrap>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Fonts />
      <Page>
        <PageHeader>
          <PageTitle>
            <span style={{ fontSize: 24 }}>👤</span>
            Gestione Utenti
          </PageTitle>
        </PageHeader>

        <Body>
          {/* ─── Form card ─────────────────────────────────────────── */}
          <Card>
            <CardTitle>
              {editingId ? <FaEdit size={14} /> : <FaPlus size={14} />}
              {editingId ? "Modifica utente" : "Nuovo utente"}
            </CardTitle>
            <form onSubmit={handleSubmit}>
              <FieldGroup>
                <div>
                  <Label htmlFor="username">Username</Label>
                  <InputWrap>
                    <InputIcon><FaUser size={12} /></InputIcon>
                    <Input
                      id="username"
                      hasIcon
                      type="text"
                      placeholder="Es. mario.rossi"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </InputWrap>
                </div>

                <div>
                  <Label htmlFor="password">
                    Password{editingId && <span style={{ fontWeight: 400, textTransform: "none", marginLeft: 4 }}>(opzionale)</span>}
                  </Label>
                  <InputWrap>
                    <InputIcon><FaKey size={12} /></InputIcon>
                    <Input
                      id="password"
                      hasIcon
                      type="password"
                      placeholder={editingId ? "Lascia vuoto per non cambiare" : "Password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required={!editingId}
                    />
                  </InputWrap>
                  {editingId && (
                    <PasswordHint>Lascia vuoto per mantenere la password attuale</PasswordHint>
                  )}
                </div>

                <div>
                  <Label>Ruolo</Label>
                  <RoleToggle>
                    <RoleOption
                      type="button"
                      active={role === "user" ? 1 : 0}
                      onClick={() => setRole("user")}
                    >
                      <FaUser size={11} />
                      User
                    </RoleOption>
                    <RoleOption
                      type="button"
                      active={role === "admin" ? 1 : 0}
                      onClick={() => setRole("admin")}
                    >
                      <FaUserShield size={11} />
                      Admin
                    </RoleOption>
                  </RoleToggle>
                </div>
              </FieldGroup>

              <FormActions>
                <Btn variant="primary" type="submit" style={{ flex: 1, justifyContent: "center" }}>
                  {editingId ? "Aggiorna" : "Aggiungi"}
                </Btn>
                {editingId && (
                  <Btn variant="ghost" type="button" onClick={resetForm}>
                    Annulla
                  </Btn>
                )}
              </FormActions>
            </form>
          </Card>

          {/* ─── User list ─────────────────────────────────────────── */}
          <Card>
            <CardTitle>
              <span>👥</span>
              Utenti registrati
              <span style={{ marginLeft: "auto", fontWeight: 400, fontSize: 13, color: C.muted }}>
                {users.length} {users.length === 1 ? "utente" : "utenti"}
              </span>
            </CardTitle>
            {users.length === 0 ? (
              <EmptyState>Nessun utente registrato.<br />Aggiungine uno con il form a sinistra.</EmptyState>
            ) : (
              <UserList>
                {users.map((u) => (
                  <UserCard key={u.id} editing={editingId === u.id ? 1 : 0}>
                    <UserAvatar isAdmin={u.role === "admin"}>
                      {u.username.charAt(0).toUpperCase()}
                    </UserAvatar>
                    <UserInfo>
                      <UserName>{u.username}</UserName>
                      <RoleBadge isAdmin={u.role === "admin"}>
                        {u.role === "admin" ? <FaUserShield size={9} /> : <FaUser size={9} />}
                        {u.role}
                      </RoleBadge>
                    </UserInfo>
                    <UserActions>
                      <IconBtn variant="edit" title="Modifica" onClick={() => handleEdit(u)}>
                        <FaEdit />
                      </IconBtn>
                      <IconBtn variant="delete" title="Elimina" onClick={() => handleDelete(u.id)}>
                        <FaTrash />
                      </IconBtn>
                    </UserActions>
                  </UserCard>
                ))}
              </UserList>
            )}
          </Card>
        </Body>

        <ToastContainer position="bottom-right" autoClose={3000} />
      </Page>
    </>
  );
}

export default Utenti;
